<template>
  <div>
    <div>
      <page-title :value="$t('schoolService.monthlyUsage')" />
    </div>
    <div class="my-4">
      <month-pager v-model="yearMonth" />
    </div>
    <div class="table-wrapper">
      <div class="usage-table" :style="tableStyle">
        <div class="date-labels">
          <div class="date-label col-name"></div>
          <div class="date-label col-date">{{ $t('schoolService.monthly') }}</div>
          <div class="date-label col-date" v-for="d in dateLabels" :key="d">
            {{ d }}
          </div>
        </div>
        <div
          class="child-row"
          v-for="cwu in childrenWithUsages"
          :key="cwu.child.childId"
        >
          <div class="child-row__name">
            {{ cwu.child.lastName }} {{ cwu.child.firstName }}
          </div>
          <div
            class="child-row_items"
            v-for="service in cwu.services"
            :key="service.service.uniqueServiceId"
          >
            <div class="table-item col-name">
              {{ service.service.uniqueServiceName }}
            </div>
            <div class="table-item col-date">
              <template v-if="service.isMonthly">
                <v-icon v-if="service.isMonthlyUsed" color="primary"
                  >mdi-checkbox-marked</v-icon
                >
                <v-icon v-else color="#c8c8c8"
                  >mdi-checkbox-blank-outline</v-icon
                >
              </template>
            </div>
            <!-- 月額 -->
            <div
              class="table-item col-date"
              v-for="usage in service.usages"
              :key="usage.date"
            >
              <template v-if="!service.isMonthly">
                <v-icon v-if="usage.hasUsed" color="primary"
                  >mdi-checkbox-marked</v-icon
                >
                <v-icon v-else color="#c8c8c8"
                  >mdi-checkbox-blank-outline</v-icon
                >
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import PageTitle from "@/components/atoms/PageTitle.vue";
import MonthPager from "@/components/atoms/MonthPager.vue";
import dayjs from "dayjs";

import { schoolServiceMonthlyUsageModule } from "@/store/viewModules/school-service/schoolServiceMonthlyUsageModule";
import {
  NurseryChildResponse,
  UniqueServiceMastResponse,
  UniqueServiceRegistrationResponse,
} from "chaild-api/lib";

interface DataType {
  yearMonth: string;

  isFetching: boolean;
}

interface ChildWithUsages {
  child: NurseryChildResponse;
  services: ServiceWithUsages[];
}

interface ServiceWithUsages {
  service: UniqueServiceMastResponse;
  usages: DateWithUsage[];
  isMonthly: boolean;
  isMonthlyUsed: boolean;
}

interface DateWithUsage {
  date: string;
  dateLabel: string;
  hasUsed: boolean;
}

export default defineComponent({
  components: {
    PageTitle,
    MonthPager,
  },

  data: (): DataType => ({
    yearMonth: dayjs().startOf('month').format("YYYY-MM-DD"),

    isFetching: false,
  }),

  computed: {
    dates(): string[] {
      const date = dayjs(this.yearMonth).startOf("months");
      this.listUsages();
      const daysInMonth = date.daysInMonth();
      return [...Array(daysInMonth)].map((emp, index) => {
        return date.add(index, "day").format("YYYY-MM-DD");
        // return date.add(index, 'day').format('MM/DD (ddd)');
      });
    },
    dateLabels(): string[] {
      return this.dates.map((d) => dayjs(d).format("MM/DD (ddd)"));
    },
    tableStyle(): string {
      const tableWidth = 180 + 80 * (this.dates.length + 1);
      return `width: ${tableWidth}px;`;
    },
    usages() {
      return schoolServiceMonthlyUsageModule.usages;
    },
    services() {
      return schoolServiceMonthlyUsageModule.services;
    },
    childrenWithUsages(): ChildWithUsages[] {
      return this.usages.map((usage) => {
        const services: ServiceWithUsages[] = this.services.filter(service => service.isHidden !== 1).map((service) => {
          const findService = usage.uniqueServices.find(
            (s) => s.uniqueServiceId === service.uniqueServiceId
          );

          const registrations = findService ? findService.registrations : [];

          const datesWithUsages: DateWithUsage[] = this.dates.map((date) => {
            const findUsage = registrations.find((r) => r.date === date);
            const hasUsed = findUsage ? true : false;
            return {
              date,
              dateLabel: dayjs(date).format("MM/DD (ddd)"),
              hasUsed,
            };
          });

          const isMonthlyUsed = findService
            ? findService.registrations.length > 0
            : false;

          return {
            service,
            usages: datesWithUsages,
            isMonthly: service.serviceType !== "perUse",
            isMonthlyUsed,
          };
        });
        return {
          child: usage.child,
          services,
        };
      });
    },
  },

  methods: {
    async listUsages() {
      this.isFetching = true;
      if (this.yearMonth.split('-').length == 2) {
        this.yearMonth += '-01';
      }
      await schoolServiceMonthlyUsageModule.listUsages({
        yearMonth: this.yearMonth,
      });
      this.isFetching = false;
    },
  },

  mounted() {
    const classIdParam = this.$route.params.classId;
    const classIdStr = typeof classIdParam === "string" ? classIdParam : classIdParam?.[0];
    if (classIdStr) {
      const classId = parseInt(classIdStr, 10);
      schoolServiceMonthlyUsageModule.setClassId(classId);
      schoolServiceMonthlyUsageModule.listSchoolServices();
    }
  },
});
</script>
<style lang="scss" scoped>
.date-labels {
  display: flex;
}

.table-wrapper {
  overflow: hidden;
  overflow-x: scroll;
}

.date-label {
  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 12px;
  font-weight: bold;
}

.col-name {
  width: 180px;
  border-right: solid 1px #e0e0e0;
  min-width: 180px;
  white-space: normal; 
  word-break: break-word; 
  overflow-wrap: break-word;
}

.col-date {
  width: 80px;
}

.child-row {
  margin: 8px 0;
}

.child-row__name {
  width: 100%;
  background-color: #f0f0f0;
  padding: 4px;

  font-size: 14px;
  font-weight: bold;
}

.child-row_items {
  height: 40px;

  display: flex;
  align-items: center;
}

.table-item {
  &.col-date {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &.col-name {
    font-size: 14px;
  }
}
</style>
